import type { LocationState } from 'redux-first-router';
import type { AnyAction, Dispatch } from 'redux';
import {
  fetchPortfolioAtLoad,
  fetchPortfolioNewsAtLoad,
  fetchPublicPortfolioNewsAtLoad,
} from '@/components/DeprecatedPortfolio/redux/actions';
import { fetchPublicPortfolio } from '@/components/DeprecatedPortfolio/redux/routines';
import {
  fetchCompanies,
  fetchCompany,
} from '@/pages/CompanyReport/redux/routines';
import { detectMarket } from '@ducks/shared/routines';

import {
  ROUTE_ABOUT,
  ROUTE_ACCOUNT_RECOVERY,
  ROUTE_ACCOUNT_VALIDATION,
  ROUTE_AFFILIATE_PROGRAM,
  ROUTE_ARTICLE,
  ROUTE_BUSINESS,
  ROUTE_CAREERS,
  ROUTE_COMMUNITY_IDEA,
  ROUTE_COMPANY,
  ROUTE_COMPANY_WITH_EVENTS,
  ROUTE_COMPARE_COMPANY,
  ROUTE_COMPARE_HOME,
  ROUTE_CREATE_CRITERIA,
  ROUTE_DASHBOARD,
  ROUTE_DASHBOARD_WITH_EVENTS,
  ROUTE_DELETE_ACCOUNT,
  ROUTE_DEPRECATED_IMPORT_PORTFOLIO,
  ROUTE_DEPRECATED_PORTFOLIO,
  ROUTE_DEPRECATED_PORTFOLIO_EDIT_TXN,
  ROUTE_DEPRECATED_PORTFOLIO_LIST,
  ROUTE_DEPRECATED_PORTFOLIO_RETURNS_REPORT,
  ROUTE_DEPRECATED_PORTFOLIO_SHARE,
  ROUTE_DEPRECATED_PUBLIC_PORTFOLIO,
  ROUTE_MY_PORTFOLIOS,
  ROUTE_DISCOVER_INVESTING_IDEAS,
  ROUTE_DISCOVER_SAMPLE_PORTFOLIOS,
  ROUTE_EDIT_EXPLORE_IDEA,
  ROUTE_EDIT_USER_IDEA,
  ROUTE_HOME,
  ROUTE_INVESTING_IDEA_DISCOVERY_PAGE,
  ROUTE_INVESTING_IDEA_DISCOVERY_PAGE_HOME,
  ROUTE_INVESTING_IDEA_HOME,
  ROUTE_INVESTING_IDEA_MARKET,
  ROUTE_INVESTING_IDEA_MARKET_INDUSTRY,
  ROUTE_MAINTENANCE,
  ROUTE_MARKET,
  ROUTE_MARKET_HOME,
  ROUTE_MARKET_HUB,
  ROUTE_MARKET_SECTOR,
  ROUTE_MARKET_SECTOR_SECONDARY,
  ROUTE_MARKET_SECTOR_SECONDARY_TERTIARY,
  ROUTE_NARRATIVE,
  ROUTE_NARRATIVE_CREATION,
  ROUTE_NARRATIVE_EDITION,
  ROUTE_NARRATIVE_LIST,
  ROUTE_OOPS,
  ROUTE_OTP_LOGIN,
  ROUTE_PLANS,
  ROUTE_PODCASTS,
  ROUTE_PORTFOLIO,
  ROUTE_PORTFOLIO_DEFAULT,
  ROUTE_PORTFOLIO_DEMO,
  ROUTE_PORTFOLIO_LINKING_SUCCESS,
  ROUTE_PORTFOLIO_NEW,
  ROUTE_PRIVACY_POLICY,
  ROUTE_PRIVACY_POLICY_FULL,
  ROUTE_PRO_API,
  ROUTE_STOCKS_HOME,
  ROUTE_STOCKS_MARKET,
  ROUTE_STOCKS_MARKET_INDUSTRY,
  ROUTE_TERMS_AND_CONDITIONS,
  ROUTE_TERMS_AND_CONDITIONS_FULL,
  ROUTE_USER_BETA_LAB,
  ROUTE_USER_LOGOUT,
  ROUTE_USER_NOTIFICATIONS,
  ROUTE_USER_PLAN,
  ROUTE_USER_PROFILE,
  ROUTE_USER_REGISTER,
  ROUTE_USER_SUBSCRIPTION,
  ROUTE_USER_SUBSCRIPTION_AND_BILLING,
  ROUTE_USER_UNSUBSCRIBE,
  ROUTE_WATCHLIST,
  ROUTE_WATCHLIST_WITH_EVENTS,
  ROUTE_WELCOME,
  ROUTE_LOCALE_COMPANY,
  ROUTE_LOCALE_COMPANY_WITH_EVENTS,
  ROUTE_PUBLIC_NARRATIVE_CREATE,
  ROUTE_PUBLIC_NARRATIVE_EDIT,
  ROUTE_PARTNER_REGISTER,
  ROUTE_PARTNER_LOGIN,
  ROUTE_PARTNER_ERROR,
  ROUTE_PARTNER_CLAIM,
  ROUTE_SUBSCRIPTION_EXTENSION,
  ROUTE_PASSWORD_RESET,
  ROUTE_SOCIAL_TO_LOCAL,
  ROUTE_EMAIL_CHANGE,
} from '@/constants/routes';
import {
  TRACKING_ARTICLE_PAGE,
  TRACKING_COMMUNITY_IDEA,
  TRACKING_COMPANY,
  TRACKING_DASHBOARD,
  TRACKING_DISCOVER_INVESTING_IDEAS,
  TRACKING_DISCOVER_SAMPLE_PORTFOLIOS,
  TRACKING_HOME,
  TRACKING_INVESTING_IDEA,
  TRACKING_MARKET_HUB,
  TRACKING_MARKET_PAGE,
  TRACKING_PORTFOLIO,
  TRACKING_PORTFOLIO_NEW,
  TRACKING_PORTFOLIO_DEMO,
  TRACKING_SCREENER,
  TRACKING_STOCKS_HOME,
  TRACKING_USER_BETA_LAB,
  TRACKING_USER_LOGOUT,
  TRACKING_USER_NOTIFICATIONS,
  TRACKING_USER_PLAN,
  TRACKING_USER_PORTFOLIO,
  TRACKING_USER_PROFILE,
  TRACKING_USER_REGISTER,
  TRACKING_USER_SUBSCRIPTION,
  TRACKING_USER_SUBSCRIPTION_AND_BILLING,
  TRACKING_WATCHLIST,
  TRACKING_PORTFOLIO_LINKING_SUCCESS,
} from '@/constants/tracking';
import type { SortBy } from '@/pages/Stocks/hooks/useSortByOptions';
import { discoveryPageIdList } from '@/pages/DiscoverPageV2/constants';
import { routesFeatureFlags } from './routesFeatureFlags';
import { setPreferredLanguage } from '@simplywallst/services';
import { setPreferredLanguage as setPreferredLanguageGQL } from '@/utilities/graphQLClient';
import { SwsTeam } from '@/components/Sentry';
import {
  SUB_ROUTE_CALENDAR,
  SUB_ROUTE_PERFORMANCE,
  SUB_ROUTE_SCREENERS,
  SUB_ROUTE_UPDATES,
} from '@/components/Dashboard/redux/constants';

export interface State {
  location: LocationState;
}

interface LayoutObject {
  desktopTopbar?: boolean;
  mobileTopbar?: boolean;
  mobileFooterMenu?: boolean;
  mobileTopbarSubNav?: boolean;
}

type RouteAction = (payload: any, state: State) => AnyAction;

interface RouteObject {
  name: string;
  path: string;
  actions?: RouteAction[];
  needsAuthentication?: boolean;
  dispatcher?: (
    dispatch: Dispatch,
    getState: () => State,
    action?: RouteAction[]
  ) => void;
  layout?: LayoutObject;
  pageName?: string;
  team?: SwsTeam;
}

type RouteThunk = (
  route: RouteObject
) => (dispatch: Dispatch, getState: () => State) => void;

export interface Route {
  path: string;
  capitalizedWords: boolean;
  thunk: ReturnType<RouteThunk>;
  layout: null | LayoutObject;
  pageName?: string;
  needsAuthentication?: boolean;
  team?: SwsTeam;
}

export type RoutesMap = Record<RouteObject['name'], Route>;

/**
 * Dashboard routes are subject to API Gateway configuration. If you need to add a
 * dashboard sub-route, as well as updating this regex, you'll need to update the
 * terraform configuration to specify the new sub-route as an application route.
 * See mono/terraform/main.tf for more details.
 */
const dashboardSubRoutesRegex = [
  SUB_ROUTE_UPDATES,
  SUB_ROUTE_SCREENERS,
  SUB_ROUTE_PERFORMANCE,
  SUB_ROUTE_CALENDAR,
].join('|');

export const sortByList: SortBy[] = [
  'market-cap-large',
  'market-cap-small',
  'top-gainers',
  'biggest-losers',
  'growth',
  'dividend-yield-high',
];
const sortByRouteMatcherRegex = sortByList.join('|');
/**
 * Discovery Page WIP
 * To allow specific discovery page ids through to route
 */
const discoveryPageRouteMatcherRegex = discoveryPageIdList.join('|');

const routeThunk: RouteThunk = (route) => async (dispatch, getState) => {
  const state = getState();
  const { location } = state;

  const currentLocale = location.payload?.locale || 'en';
  try {
    setPreferredLanguage(currentLocale);
    setPreferredLanguageGQL(currentLocale);
  } catch {
    /** silently ignore */
  }
  try {
    if (
      decodeURIComponent(location.pathname) !==
      decodeURIComponent(location.prev.pathname)
    ) {
      if (route.dispatcher) {
        // sometimes routes want custom actions on load
        route.dispatcher(dispatch, getState, route.actions);
      } else if (route.actions) {
        route.actions.forEach((action) => dispatch(action(location, state)));
      }
    }
    // And this too?
    await Promise.resolve('Thunk ran...');
  } catch (err) {
    console.error('Failed to run thunk..', err);
  }
};

const filterRoutes = (routes: RouteObject[]) => {
  const routesWithFeatureFlags = routesFeatureFlags();

  return routes.filter((r) => {
    if (r.name in routesWithFeatureFlags) {
      return routesWithFeatureFlags[r.name];
    }
    return true;
  });
};

const mapRoutes = (routes: RouteObject[]): RoutesMap =>
  routes.reduce((acc, current) => {
    acc[current.name] = {
      path: current.path,
      capitalizedWords: false,
      thunk: routeThunk(current),
      layout: current.layout || null,
      pageName: current.pageName || undefined,
      needsAuthentication: current.needsAuthentication,
      team: current.team,
    };
    return acc;
  }, {});

const routes: RouteObject[] = [
  {
    name: ROUTE_HOME,
    path: '/',
    pageName: TRACKING_HOME,
    needsAuthentication: false,
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
  },
  {
    name: ROUTE_OOPS,
    path: '/oops',

    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
    needsAuthentication: false,
  },
  {
    name: ROUTE_WELCOME,
    path: '/welcome',
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
    needsAuthentication: false,
  },
  {
    name: ROUTE_PARTNER_CLAIM,
    path: '/partner/claim',
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
    needsAuthentication: false,
  },
  {
    name: ROUTE_PARTNER_REGISTER,
    path: '/partner/register',
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
    needsAuthentication: false,
  },
  {
    name: ROUTE_PARTNER_LOGIN,
    path: '/partner/login',
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
    needsAuthentication: false,
  },
  {
    name: ROUTE_PARTNER_ERROR,
    path: '/partner/error',
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
    needsAuthentication: false,
  },
  {
    name: ROUTE_DEPRECATED_PORTFOLIO,
    path: '/portfolio/my-portfolios/:portfolioId/:name?',
    pageName: TRACKING_PORTFOLIO,
    layout: {
      mobileTopbarSubNav: true,
    },
    actions: [fetchPortfolioAtLoad, fetchPortfolioNewsAtLoad],
    needsAuthentication: true,
    team: 'wombats',
  },
  {
    name: ROUTE_DEPRECATED_PUBLIC_PORTFOLIO,
    path: '/portfolio/discover/:portfolioId/:name',
    layout: {
      mobileTopbarSubNav: true,
    },
    actions: [fetchPublicPortfolio.trigger, fetchPublicPortfolioNewsAtLoad],
    needsAuthentication: false,
    team: 'wombats',
  },
  {
    name: ROUTE_DEPRECATED_PORTFOLIO_LIST,
    path: '/portfolio',
    needsAuthentication: false,
    team: 'wombats',
  },
  {
    name: ROUTE_PORTFOLIO_DEFAULT,
    path: '/portfolio/v2',
    needsAuthentication: true,
    team: 'wombats',
  },
  {
    name: ROUTE_PORTFOLIO_DEMO,
    path: '/portfolio/demo',
    pageName: TRACKING_PORTFOLIO_DEMO,
    needsAuthentication: false,
    team: 'wombats',
  },
  {
    name: ROUTE_PORTFOLIO_NEW,
    path: '/portfolio/new',
    pageName: TRACKING_PORTFOLIO_NEW,
    needsAuthentication: true,
    team: 'wombats',
  },
  {
    name: ROUTE_PORTFOLIO_LINKING_SUCCESS,
    path: '/portfolio/linking-success/:provider?',
    pageName: TRACKING_PORTFOLIO_LINKING_SUCCESS,
    needsAuthentication: true,
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
    team: 'wombats',
  },
  {
    name: ROUTE_MY_PORTFOLIOS,
    path: '/portfolio/my-portfolios',
    pageName: TRACKING_USER_PORTFOLIO,
    needsAuthentication: true,
    team: 'wombats',
  },
  {
    name: ROUTE_PORTFOLIO,
    path: '/portfolio/:portfolioId',
    pageName: TRACKING_PORTFOLIO,
    needsAuthentication: true,
    team: 'wombats',
  },
  {
    name: ROUTE_DISCOVER_INVESTING_IDEAS,
    path: '/discover/investing-ideas',
    pageName: TRACKING_DISCOVER_INVESTING_IDEAS,
    layout: {
      mobileTopbarSubNav: true,
    },
    needsAuthentication: false,
    team: 'dingoes',
  },
  // DiscoverPageV2 ***IMPORTANT*** DiscoverPageV2 should come before DiscoverPageV1 due to :id(${discoveryPageRouteMatcherRegex})
  {
    name: ROUTE_INVESTING_IDEA_DISCOVERY_PAGE_HOME,
    path: `/discover/investing-ideas/:id(${discoveryPageRouteMatcherRegex})/:name`,
    actions: [detectMarket.trigger],
    needsAuthentication: false,
    team: 'dingoes',
  },
  {
    name: ROUTE_INVESTING_IDEA_DISCOVERY_PAGE,
    path: `/discover/investing-ideas/:id(${discoveryPageRouteMatcherRegex})/:name/:market?/:industry?`,
    pageName: TRACKING_INVESTING_IDEA,
    needsAuthentication: false,
    team: 'dingoes',
  },
  {
    name: ROUTE_INVESTING_IDEA_HOME,
    path: '/discover/investing-ideas/:id/:name',
    actions: [detectMarket.trigger],
    needsAuthentication: false,
    team: 'dingoes',
  },
  {
    name: ROUTE_INVESTING_IDEA_MARKET,
    path: '/discover/investing-ideas/:id/:name/:market',
    pageName: TRACKING_INVESTING_IDEA,
    needsAuthentication: false,
    team: 'dingoes',
  },
  {
    name: ROUTE_INVESTING_IDEA_MARKET_INDUSTRY,
    path: '/discover/investing-ideas/:id/:name/:market/:industry',
    pageName: TRACKING_INVESTING_IDEA,
    needsAuthentication: false,
    team: 'dingoes',
  },
  {
    name: ROUTE_COMMUNITY_IDEA,
    path: `/discover/community-ideas/:id/:name`,
    pageName: TRACKING_COMMUNITY_IDEA,
    needsAuthentication: false,
    team: 'dingoes',
  },
  {
    name: ROUTE_DISCOVER_SAMPLE_PORTFOLIOS,
    path: '/discover/sample-portfolios',
    pageName: TRACKING_DISCOVER_SAMPLE_PORTFOLIOS,
    layout: {
      mobileTopbarSubNav: true,
    },
    needsAuthentication: false,
    team: 'wombats',
  },
  {
    name: ROUTE_DASHBOARD,
    pageName: TRACKING_DASHBOARD,
    path: `/dashboard/:subRoute(${dashboardSubRoutesRegex})?`,
    layout: {
      mobileTopbarSubNav: true,
    },
    needsAuthentication: true,
  },
  {
    name: ROUTE_DASHBOARD_WITH_EVENTS,
    pageName: TRACKING_DASHBOARD,
    path: `/dashboard/:subRoute(${dashboardSubRoutesRegex})?/:event/:eventId?`,
    layout: {
      mobileTopbarSubNav: true,
    },
    needsAuthentication: true,
  },
  {
    name: ROUTE_COMPANY,
    path: '/stocks/:isoCode2/:industry/:exchange_ticker/:companyname/:section?',
    pageName: TRACKING_COMPANY,
    layout: {
      mobileTopbarSubNav: true,
    },
    actions: [fetchCompany.trigger],
    needsAuthentication: false,
    team: 'dingoes',
  },
  {
    name: ROUTE_COMPANY_WITH_EVENTS,
    path: '/stocks/:isoCode2/:industry/:exchange_ticker/:companyname/event/:eventId?',
    pageName: TRACKING_COMPANY,
    layout: {
      mobileTopbarSubNav: true,
    },
    actions: [fetchCompany.trigger],
    needsAuthentication: false,
    team: 'dingoes',
  },
  {
    name: ROUTE_LOCALE_COMPANY,
    path: '/:locale/stocks/:isoCode2/:industry/:exchange_ticker/:companyname/:section?',
    pageName: TRACKING_COMPANY,
    layout: {
      mobileTopbarSubNav: true,
    },
    actions: [fetchCompany.trigger],
    needsAuthentication: false,
    team: 'dingoes',
  },
  {
    name: ROUTE_LOCALE_COMPANY_WITH_EVENTS,
    path: '/:locale/stocks/:isoCode2/:industry/:exchange_ticker/:companyname/event/:eventId?',
    pageName: TRACKING_COMPANY,
    layout: {
      mobileTopbarSubNav: true,
    },
    actions: [fetchCompany.trigger],
    needsAuthentication: false,
    team: 'dingoes',
  },
  {
    name: ROUTE_COMPARE_COMPANY,
    path: '/compare/:symbols',
    actions: [fetchCompanies.trigger],
    needsAuthentication: false,
    team: 'dingoes',
  },
  {
    name: ROUTE_COMPARE_HOME,
    path: '/compare',
    needsAuthentication: false,
    team: 'dingoes',
  },
  {
    name: ROUTE_USER_PROFILE,
    path: '/user/profile',
    pageName: TRACKING_USER_PROFILE,
    layout: {
      mobileTopbarSubNav: true,
    },
    needsAuthentication: true,
  },
  {
    name: ROUTE_USER_NOTIFICATIONS,
    path: '/user/notifications',
    pageName: TRACKING_USER_NOTIFICATIONS,
    layout: {
      mobileTopbarSubNav: true,
    },
    needsAuthentication: true,
  },
  {
    name: ROUTE_USER_PLAN,
    path: '/user/plan',
    pageName: TRACKING_USER_PLAN,
    layout: {
      mobileTopbarSubNav: true,
    },
    needsAuthentication: true,
  },
  {
    name: ROUTE_USER_SUBSCRIPTION,
    path: '/user/subscription',
    pageName: TRACKING_USER_SUBSCRIPTION,
    layout: {
      mobileTopbarSubNav: true,
    },
    needsAuthentication: true,
  },
  {
    name: ROUTE_USER_SUBSCRIPTION_AND_BILLING,
    path: '/user/subscription-and-billing',
    pageName: TRACKING_USER_SUBSCRIPTION_AND_BILLING,
    layout: {
      mobileTopbarSubNav: true,
    },
    needsAuthentication: true,
  },
  {
    name: ROUTE_USER_BETA_LAB,
    path: '/user/beta-lab',
    pageName: TRACKING_USER_BETA_LAB,
    layout: {
      mobileTopbarSubNav: true,
    },
    needsAuthentication: true,
  },
  {
    name: ROUTE_USER_LOGOUT,
    path: '/logout',
    pageName: TRACKING_USER_LOGOUT,
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
  },
  {
    name: ROUTE_USER_REGISTER,
    path: '/register',
    pageName: TRACKING_USER_REGISTER,
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
  },
  {
    name: ROUTE_DEPRECATED_IMPORT_PORTFOLIO,
    path: '/user/portfolio/import',
    actions: [],
    needsAuthentication: true,
    team: 'wombats',
  },
  {
    name: ROUTE_DEPRECATED_PORTFOLIO_RETURNS_REPORT,
    path: '/user/portfolio/calculation/:id/:name',
    actions: [],
    needsAuthentication: true,
    team: 'wombats',
  },
  {
    name: ROUTE_DEPRECATED_PORTFOLIO_EDIT_TXN,
    path: '/user/portfolio/edittxn/:id/:name',
    actions: [],
    needsAuthentication: true,
    team: 'wombats',
  },
  {
    name: ROUTE_DEPRECATED_PORTFOLIO_SHARE,
    path: '/portfolio/shared-portfolios/:portfolioId/:secret',
    actions: [fetchPublicPortfolio.trigger],
    needsAuthentication: false,
    team: 'wombats',
  },
  {
    name: ROUTE_CREATE_CRITERIA,
    path: '/screener/create/:marketISO?/:industrySlug?/:name?',
    pageName: TRACKING_SCREENER,
    needsAuthentication: false,
    team: 'dingoes',
  },
  {
    name: ROUTE_EDIT_USER_IDEA,
    path: '/screener/edit/user/:id/:name?',
    pageName: TRACKING_SCREENER,
    needsAuthentication: true,
    team: 'dingoes',
  },
  {
    name: ROUTE_EDIT_EXPLORE_IDEA,
    path: '/screener/edit/explore/:id/:marketISO?/:industrySlug?',
    pageName: TRACKING_SCREENER,
    needsAuthentication: false,
    team: 'dingoes',
  },
  {
    name: ROUTE_ACCOUNT_RECOVERY,
    path: '/account-recovery',
    actions: [],
    needsAuthentication: false,
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
  },
  {
    name: ROUTE_OTP_LOGIN,
    path: '/otp-login/:token',
    actions: [],
    needsAuthentication: false,
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
  },
  {
    name: ROUTE_DELETE_ACCOUNT,
    path: '/account-delete/:token',
    actions: [],
    needsAuthentication: false,
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
  },
  {
    name: ROUTE_STOCKS_HOME,
    path: '/stocks',
    pageName: TRACKING_STOCKS_HOME,
    actions: [detectMarket.trigger],
    needsAuthentication: false,
    // TODO: Add this once experiment 'mono-sub-nav-updates' concludes successfully
    // layout: {
    //   mobileTopbarSubNav: true,
    // },
  },
  {
    name: ROUTE_STOCKS_MARKET,
    path: `/stocks/:market/:sortBy(\\${sortByRouteMatcherRegex})?`,
    pageName: TRACKING_STOCKS_HOME,
    needsAuthentication: false,
    // TODO: Add this once experiment 'mono-sub-nav-updates' concludes successfully
    // layout: {
    //   mobileTopbarSubNav: true,
    // },
  },
  {
    name: ROUTE_STOCKS_MARKET_INDUSTRY,
    path: `/stocks/:market/:industry/:sortBy(\\${sortByRouteMatcherRegex})?`,
    pageName: TRACKING_STOCKS_HOME,
    needsAuthentication: false,
    // TODO: Add this once experiment 'mono-sub-nav-updates' concludes successfully
    // layout: {
    //   mobileTopbarSubNav: true,
    // },
  },
  {
    name: ROUTE_MAINTENANCE,
    path: '/maintenance',
    needsAuthentication: false,
  },
  {
    name: ROUTE_PLANS,
    path: '/plans',
    needsAuthentication: false,
    layout: {
      desktopTopbar: false,
      mobileTopbar: RUNTIME_ENV === 'native',
      mobileFooterMenu: RUNTIME_ENV === 'native',
      mobileTopbarSubNav: RUNTIME_ENV === 'native',
    },
  },
  {
    name: ROUTE_ABOUT,
    path: '/about',
    needsAuthentication: false,
    actions: [],
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
  },
  {
    name: ROUTE_CAREERS,
    path: '/careers',
    needsAuthentication: false,
    actions: [],
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
  },
  {
    name: ROUTE_AFFILIATE_PROGRAM,
    path: '/affiliate-program',
    needsAuthentication: false,
    actions: [],
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
  },
  {
    name: ROUTE_USER_UNSUBSCRIBE,
    path: '/unsubscribe',
    needsAuthentication: false,
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
  },
  {
    name: ROUTE_WATCHLIST,
    path: '/watchlist',
    pageName: TRACKING_WATCHLIST,
    needsAuthentication: true,
  },
  {
    name: ROUTE_WATCHLIST_WITH_EVENTS,
    pageName: TRACKING_WATCHLIST,
    path: '/watchlist/:subRoute?/:event/:eventId?',
    layout: {
      mobileTopbarSubNav: true,
    },
    needsAuthentication: true,
  },
  {
    name: ROUTE_PODCASTS,
    path: '/podcasts',
    needsAuthentication: false,
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
  },
  {
    name: ROUTE_TERMS_AND_CONDITIONS,
    path: '/termsandconditions',
    needsAuthentication: false,
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
  },
  {
    name: ROUTE_TERMS_AND_CONDITIONS_FULL,
    path: '/terms-and-conditions',
    needsAuthentication: false,
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
  },
  {
    name: ROUTE_PRIVACY_POLICY,
    path: '/privacypolicy',
    needsAuthentication: false,
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
  },
  {
    name: ROUTE_PRIVACY_POLICY_FULL,
    path: '/privacy-policy',
    needsAuthentication: false,
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
  },
  {
    name: ROUTE_ARTICLE,
    path: '/article/:slug',
    pageName: TRACKING_ARTICLE_PAGE,
    needsAuthentication: false,
  },
  {
    name: ROUTE_MARKET_HOME,
    path: '/markets',
    pageName: TRACKING_MARKET_PAGE,
    actions: [detectMarket.trigger],
    needsAuthentication: false,
  },
  {
    name: ROUTE_MARKET_HUB,
    path: '/markets/insights',
    pageName: TRACKING_MARKET_HUB,
    needsAuthentication: false,
  },
  {
    name: ROUTE_MARKET,
    path: '/markets/:market',
    pageName: TRACKING_MARKET_PAGE,
    needsAuthentication: false,
  },
  {
    name: ROUTE_MARKET_SECTOR,
    path: '/markets/:market/:sector',
    pageName: TRACKING_MARKET_PAGE,
    needsAuthentication: false,
  },
  {
    name: ROUTE_MARKET_SECTOR_SECONDARY,
    path: '/markets/:market/:sector/:secondary',
    pageName: TRACKING_MARKET_PAGE,
    needsAuthentication: false,
  },
  {
    name: ROUTE_MARKET_SECTOR_SECONDARY_TERTIARY,
    path: '/markets/:market/:sector/:secondary/:tertiary',
    pageName: TRACKING_MARKET_PAGE,
    needsAuthentication: false,
  },
  // the /business sub route is managed outside of mono for the
  // business login portal
  {
    name: ROUTE_BUSINESS,
    path: '/business',
    pageName: TRACKING_HOME,
    needsAuthentication: false,
  },
  {
    name: ROUTE_ACCOUNT_VALIDATION,
    path: '/account-validation',
    pageName: 'account-validation',
    needsAuthentication: false,
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
  },
  {
    name: ROUTE_PRO_API,
    path: '/user/pro',
    pageName: 'token-management',
    needsAuthentication: true,
    layout: {
      mobileTopbarSubNav: true,
    },
  },
  // Narwhals: these /narratives/manage routes are used
  // by internal SWS users and will only exist temporarily
  {
    name: ROUTE_NARRATIVE_CREATION,
    path: '/narratives/manage/new',
    pageName: 'narrative-creation',
    needsAuthentication: true,
    layout: {
      mobileTopbarSubNav: false,
    },
    team: 'narwhals',
  },
  {
    name: ROUTE_NARRATIVE_EDITION,
    path: '/narratives/manage/:slug',
    pageName: 'narrative-edition',
    needsAuthentication: true,
    layout: {
      mobileTopbarSubNav: false,
    },
    team: 'narwhals',
  },
  {
    name: ROUTE_NARRATIVE_LIST,
    path: '/narratives/manage',
    pageName: 'narratives',
    needsAuthentication: true,
    layout: {
      mobileTopbarSubNav: false,
    },
    team: 'narwhals',
  },
  {
    name: ROUTE_PUBLIC_NARRATIVE_CREATE,
    path: '/narratives/new',
    pageName: 'public-narrative-create',
    needsAuthentication: true,
    layout: {
      mobileTopbarSubNav: false,
    },
    team: 'narwhals',
  },
  {
    name: ROUTE_PUBLIC_NARRATIVE_EDIT,
    path: '/narratives/edit/:id',
    pageName: 'public-narrative-edit',
    needsAuthentication: true,
    layout: {
      mobileTopbarSubNav: false,
    },
    team: 'narwhals',
  },
  {
    name: ROUTE_NARRATIVE,
    path: '/narratives/:slug',
    pageName: 'narrative',
    needsAuthentication: false,
    layout: {
      mobileTopbarSubNav: false,
    },
    team: 'narwhals',
  },
  {
    name: ROUTE_SUBSCRIPTION_EXTENSION,
    path: '/subscription-extension',
    needsAuthentication: true,
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
  },
  {
    name: ROUTE_PASSWORD_RESET,
    path: '/password-reset',
    needsAuthentication: false,
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
  },
  {
    name: ROUTE_SOCIAL_TO_LOCAL,
    path: '/social-to-local',
    needsAuthentication: false,
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
  },
  {
    name: ROUTE_EMAIL_CHANGE,
    path: '/email-change',
    needsAuthentication: false,
    layout: {
      desktopTopbar: false,
      mobileTopbar: false,
      mobileFooterMenu: false,
    },
  },
];

export default function routesGenerator() {
  return mapRoutes(filterRoutes(routes));
}
